import React, { useState } from 'react';
import Papa from 'papaparse';
import { Contact } from '../../types';
import { api } from '../../services/api';
import { Upload, Check, ArrowLeft, Info, CheckCircle, XCircle, X } from 'lucide-react';

interface ContactImporterProps {
  onImportComplete?: () => void;
  onClose?: () => void;
}

interface ResultDetail {
  name: string;
  status: 'success' | 'error';
  message: string;
}

const ContactImporter: React.FC<ContactImporterProps> = ({ onImportComplete, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [preview, setPreview] = useState<Record<string, any>[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [results, setResults] = useState<{ success: number; error: number; details: ResultDetail[] } | null>(null);
  const [step, setStep] = useState(1);
  const [error, setError] = useState<string>('');

  const requiredFields = ['name'];
  const availableFields = [
    '',
    'name', 'email', 'phone', 'notes', 'birthday', 
    'groups', 'linkedin', 'facebook', 'telegram', 'xcom'
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setFileName(selectedFile.name);
    setError('');
    setPreview([]);
    setCsvHeaders([]);
    setMapping({});
    setResults(null);
    
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      preview: 5,
      complete: (results) => {
        if (results.data.length === 0 || !results.meta.fields || results.meta.fields.length === 0) {
          setError('The file appears to be empty or has no header row.');
          setFile(null);
          setFileName('');
          return;
        }
        
        const headers = results.meta.fields;
        setCsvHeaders(headers);
        setPreview(results.data as Record<string, any>[]);

        const initialMapping: Record<string, string> = {};
        headers.forEach(header => {
          const normalizedHeader = header.toLowerCase().trim().replace(/[^a-z0-9]/g, '');
          if (normalizedHeader.includes('name')) initialMapping[header] = 'name';
          else if (normalizedHeader.includes('email')) initialMapping[header] = 'email';
          else if (normalizedHeader.includes('phone')) initialMapping[header] = 'phone';
          else if (normalizedHeader.includes('note')) initialMapping[header] = 'notes';
          else if (normalizedHeader.includes('birth')) initialMapping[header] = 'birthday';
          else if (normalizedHeader.includes('group')) initialMapping[header] = 'groups';
          else if (normalizedHeader.includes('linkedin')) initialMapping[header] = 'linkedin';
          else if (normalizedHeader.includes('facebook')) initialMapping[header] = 'facebook';
          else if (normalizedHeader.includes('telegram')) initialMapping[header] = 'telegram';
          else if (normalizedHeader.includes('twitter') || normalizedHeader.includes('xcom')) initialMapping[header] = 'xcom';
          else initialMapping[header] = '';
        });
        setMapping(initialMapping);
        setStep(2);
      },
      error: (error) => {
        setError(`Error parsing file: ${error.message}`);
        setFile(null);
        setFileName('');
      }
    });
  };

  const handleMappingChange = (csvField: string, appField: string) => {
    setMapping(prev => ({ ...prev, [csvField]: appField }));
  };

  const validateMapping = (): boolean => {
    return Object.values(mapping).includes('name');
  };

  const handleImport = async () => {
    if (!file) return;
    if (!validateMapping()) {
      setError('The "name" field is required. Please map a column to the name field.');
      return;
    }
    
    setIsImporting(true);
    setError('');
    setResults({ success: 0, error: 0, details: [] });
    
    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const importResults = { success: 0, error: 0, details: [] as ResultDetail[] };
          const dataToImport = results.data as Record<string, any>[];

          for (const rowData of dataToImport) {
            const contactData: Partial<Contact> & { groups?: string[] } = {
              name: '',
              warmth: 'warm',
              groups: [], 
              notes: '',
              lastInteraction: new Date().toISOString()
            };
            let contactNameForRow = 'Unknown';

            try {
              Object.keys(mapping).forEach(csvField => {
                const appField = mapping[csvField];
                if (appField && rowData[csvField] !== undefined && rowData[csvField] !== null && rowData[csvField] !== '') {
                  const value = rowData[csvField];
                  if (appField === 'groups') {
                    contactData.groups = typeof value === 'string' ? value.split(',').map(g => g.trim()).filter(Boolean) : [String(value)];
                  } else if (appField === 'birthday') {
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                       contactData.birthday = date.toISOString().split('T')[0];
                    }
                  } else {
                    (contactData as any)[appField] = String(value);
                  }
                  if (appField === 'name') {
                    contactNameForRow = String(value);
                  }
                }
              });

              if (!contactData.name?.trim()) {
                throw new Error('Name field was mapped but is empty for this row.');
              }
              
              await api.createContact(contactData as Omit<Contact, 'id'>);
              importResults.success++;
              importResults.details.push({ name: contactNameForRow, status: 'success', message: 'Imported' });
            } catch (err) {
              importResults.error++;
              const errorMessage = err instanceof Error ? err.message : 'Failed to import';
              importResults.details.push({ name: contactNameForRow, status: 'error', message: errorMessage });
            }
          }
          
          setResults(importResults);
          setStep(3);
          setIsImporting(false);
          if (onImportComplete) onImportComplete();
        },
        error: (error) => {
          setError(`Error parsing file during import: ${error.message}`);
          setIsImporting(false);
        }
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Import failed: ${errorMessage}`);
      setIsImporting(false);
    }
  };

  const resetImporter = () => {
     setFile(null);
     setFileName('');
     setPreview([]);
     setCsvHeaders([]);
     setMapping({});
     setIsImporting(false);
     setResults(null);
     setStep(1);
     setError('');
  };

  return (
    <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 mb-6">
      <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Import Contacts from CSV</h2>
          {onClose && (
             <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" aria-label="Close importer">
                 <X size={20} />
             </button>
          )}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select a CSV file with your contact data. Ensure it has a header row.
          </p>
          
          <div>
             <label htmlFor="csv-upload" className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium cursor-pointer">
                 <Upload size={16} />
                 <span>{fileName || 'Choose CSV File'}</span>
             </label>
             <input
               id="csv-upload"
               type="file"
               accept=".csv"
               onChange={handleFileChange}
               className="sr-only"
             />
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h3 className="font-medium text-blue-800 mb-2 text-sm flex items-center gap-1.5"><Info size={16}/> Tips for CSV</h3>
            <ul className="list-disc pl-5 text-blue-700 text-xs space-y-1">
              <li>Include a header row (e.g., Name, Email, Phone, Groups).</li>
              <li>A column mapped to "Name" is required.</li>
              <li>Multiple groups can be listed in one column, separated by commas (e.g., "Friends,Work").</li>
              <li>Dates (like birthdays) should ideally be YYYY-MM-DD.</li>
            </ul>
          </div>
        </div>
      )}
      
      {step === 2 && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            Match columns from your file <code className="text-xs bg-gray-100 p-1 rounded">{fileName}</code> to the contact fields. The <span className="font-semibold text-gray-800">Name</span> field is required.
          </p>
          
          {preview.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
               <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 bg-gray-50 border-b border-gray-200">Data Preview (First 5 Rows)</h3>
               <div className="overflow-x-auto">
                 <table className="min-w-full divide-y divide-gray-200">
                   <thead className="bg-gray-50">
                     <tr>
                       {csvHeaders.map((header) => (
                         <th key={header} scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                           {header}
                         </th>
                       ))}
                     </tr>
                   </thead>
                   <tbody className="bg-white divide-y divide-gray-100">
                     {preview.map((row, rowIndex) => (
                       <tr key={rowIndex} className="hover:bg-gray-50/50">
                         {csvHeaders.map((header) => (
                           <td key={header} className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 truncate max-w-xs">
                             {row[header]}
                           </td>
                         ))}
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>
             </div>
          )}
          
          <div>
             <h3 className="text-base font-semibold mb-3 text-gray-800">Field Mapping</h3>
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                 {csvHeaders.map((header) => (
                   <div key={header} className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                     <label htmlFor={`map-${header}`} className="text-sm font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3 truncate" title={header}>
                       {header}
                     </label>
                     <select
                       id={`map-${header}`}
                       value={mapping[header] || ''}
                       onChange={(e) => handleMappingChange(header, e.target.value)}
                       className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                     >
                       {availableFields.map(field => (
                         <option key={field} value={field}>
                           {field === '' ? 'Do not import' : field.charAt(0).toUpperCase() + field.slice(1)}
                         </option>
                       ))}
                     </select>
                   </div>
                 ))}
              </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
             <button 
               onClick={() => setStep(1)} 
               className="flex items-center gap-1.5 bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
             >
               <ArrowLeft size={16} /> Back
             </button>
             <button 
               onClick={handleImport} 
               disabled={isImporting || !validateMapping()}
               className="flex items-center gap-1.5 bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
             >
                {isImporting ? 'Importing...' : 'Import Contacts'} <Check size={16} />
             </button>
          </div>
        </div>
      )}
      
      {step === 3 && results && (
        <div className="space-y-6">
          <h3 className="text-base font-semibold text-gray-800">Import Results</h3>
          <div className="flex gap-4 text-sm">
             <span className="inline-flex items-center gap-1.5 text-green-700">
                 <CheckCircle size={16}/> {results.success} contacts imported successfully.
             </span>
             {results.error > 0 && (
                 <span className="inline-flex items-center gap-1.5 text-red-700">
                     <XCircle size={16}/> {results.error} contacts failed to import.
                 </span>
             )}
          </div>
          
          {results.details.length > 0 && (
             <div className="border border-gray-200 rounded-lg overflow-hidden">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 bg-gray-50 border-b border-gray-200">Details</h4>
                 <div className="overflow-y-auto max-h-60">
                     <table className="min-w-full divide-y divide-gray-200">
                         <thead className="bg-gray-50 sticky top-0">
                         <tr>
                             <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                             <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                             <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                         </tr>
                         </thead>
                         <tbody className="bg-white divide-y divide-gray-100">
                         {results.details.map((detail, index) => (
                             <tr key={index} className={`${detail.status === 'error' ? 'bg-red-50/50' : 'hover:bg-gray-50/50'}`}>
                             <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-800 truncate max-w-xs">{detail.name}</td>
                             <td className="px-4 py-2 whitespace-nowrap text-sm">
                                 <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${detail.status === 'success' ? 'bg-tag-green-bg text-tag-green-text' : 'bg-tag-red-bg text-tag-red-text'}`}>
                                 {detail.status === 'success' ? <CheckCircle size={12} className="mr-1"/> : <XCircle size={12} className="mr-1"/>}
                                 {detail.status}
                                 </span>
                             </td>
                             <td className="px-4 py-2 text-sm text-gray-600">{detail.message}</td>
                             </tr>
                         ))}
                         </tbody>
                     </table>
                 </div>
             </div>
          )}

           <div className="flex justify-end items-center pt-4 border-t border-gray-200">
             <button 
               onClick={resetImporter} 
               className="bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
             >
               Import Another File
             </button>
           </div>
        </div>
      )}
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 text-sm">
          {error}
        </div>
      )}
    </div>
  );
};

export default ContactImporter;